import type { Card } from "@amzn/ads-blink-client";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import type { DocumentType } from "@aws-sdk/smithy-client";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "zustand";

import { CREATE_INSIGHT_TRIGGER_TEXT } from "../../../../config/insights.config";
import { InsightFamilyNameToLabelMap } from "../../../../constants/filters";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../../constants/telemetry";
import { InsightFamilyMetadataAttributes } from "../../../../hooks/insightFamilies/useInsightFamiliesFiltersAction";
import { useContentCardShown } from "../../../../hooks/misc/useContentCardShown";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../store/filters";
import { navigationStore } from "../../../../store/navigation";
import { AquilaThumbsFeedbackPlacement } from "../../../../types/aquila";
import type { KeyValue } from "../../../../types/common";
import { insightTypeLabelToValue } from "../../../../types/filters/unified";
import { InsightFamilyName } from "../../../../types/providers";
import {
    getAdvertiserId,
    getMarketplaceId,
} from "../../../../utils/permission.utils";
import { getPhoenixContentInfoFromStore } from "../../../../utils/phoenix.utils";
import {
    convertCardToPitchedContent,
    getPitchedContentDetails,
} from "../../../../utils/pitchedContentDetails.utils";
import { isExploreWithAITab } from "../../../../utils/tab.utils";
import {
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../../../utils/telemetry.utils";
import { buildSidePanelDetails } from "../../../../utils/unifiedContent.utils";
import { getSearchParams } from "../../../../utils/url.utils";
import { AquilaThumbsFeedback } from "../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { StyledActionsContainer } from "../../commonStyles";
import { CardLayoutV3 } from "../insightDetails/CardLayoutV3";
import { InsightFamilyCardAction } from "../insightFamilies/InsightFamilyCardAction";
import { InsightPackagesCardAction } from "../insightPackages/InsightPackagesCardAction";
import { MarketingNarrativeCardAction } from "../marketingNarratives/MarketingNarrativeCardAction";
import { PitchedContentCardAction } from "../pitchedContents/PitchedContentCardAction";

export interface UnifiedContentCardProps {
    content: Card;
    index: number;
    tag?: string;
}

export const UnifiedContentCard: React.FunctionComponent<
    UnifiedContentCardProps
> = ({ content, index, tag }) => {
    const [contentSubType, filters] = useStore(
        discoveryFilterStore,
        (state) => [state.getContentSubType(), state.getFilters()],
    );
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const insightsData = filters[contentSubType.provider].data;
    const activeTab = useStore(navigationStore, (state) => state.activeTab);

    const {
        identifier,
        details,
        primaryContext,
        secondaryContext,
        lastModifiedDate,
        additionalInfo,
        cardHeader,
        title,
    } = content;

    const insightType: InsightType = content.insightType as InsightType;
    const navigate = useNavigate();
    const marketplaceId = getMarketplaceId();
    const advertiserId = getAdvertiserId();

    // Log to TAKT that we showed this card
    useContentCardShown({
        insightType,
        identifier,
    });

    const handleOnDemandInsightCardClick = useCallback(() => {
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();
        logWithTakt(TELEMETRY_ID.VIEW_INSIGHT_DETAILS_CLICK, {
            ...filters,
            identifier,
            insightType,
            resultPosition: index,
            totalResults: insightsData.length,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.VIEW_INSIGHT_DETAILS_CLICK,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    content.insightType as InsightType,
                    identifier,
                ),
                customData: {
                    filters,
                    resultPosition: index,
                    totalResults: insightsData.length,
                },
            },
        );
        navigate(
            `/domains/${details?.domainId}/templates/${identifier}${getSearchParams({}, ["getPayloadFromInsight"])}`,
        );
    }, [insightsData, identifier, index]);

    const getOnDemandInsightCardAction = () => {
        return (
            <StyledActionsContainer>
                <TextButton
                    onClick={() => void handleOnDemandInsightCardClick()}
                >
                    {CREATE_INSIGHT_TRIGGER_TEXT}
                </TextButton>
                {isExploreWithAITab(activeTab) && identifier && (
                    <AquilaThumbsFeedback
                        placement={AquilaThumbsFeedbackPlacement.CARD}
                        identifier={identifier}
                        insightType={insightType}
                        configId={"G4sThumbsFeedbackConfig_StormV4"}
                    />
                )}
            </StyledActionsContainer>
        );
    };

    const getInsightFamilyCardAction = () => {
        const metadata = content.details ? content.details.metadata : undefined;
        const insightFamilyTrackingId =
            metadata?.[InsightFamilyMetadataAttributes.FAMILY_TRACKING_ID] ??
            undefined;
        const brandId =
            metadata?.[InsightFamilyMetadataAttributes.BRAND_ID] ?? undefined;
        const sidePanelDetailsForStandaloneInsights = buildSidePanelDetails(
            content,
            insightType,
        );

        return (
            <InsightFamilyCardAction
                identifier={identifier!}
                metadata={
                    metadata as Record<InsightFamilyMetadataAttributes, string>
                }
                insightFamilyTrackingId={insightFamilyTrackingId}
                details={sidePanelDetailsForStandaloneInsights}
                title={title!}
                index={index}
                brandId={brandId}
                familyName={cardHeader!}
            />
        );
    };

    const getMarketingNarrativeCardAction = () => {
        return (
            <MarketingNarrativeCardAction
                identifier={identifier!}
                index={index}
            />
        );
    };

    const getPitchedContentCardAction = () => {
        const pitchedContent = convertCardToPitchedContent(content);
        const pitchedContentDetails = getPitchedContentDetails(pitchedContent);

        return (
            <PitchedContentCardAction
                content={pitchedContent}
                details={pitchedContentDetails}
                isEditable={false}
                isSharable={false}
            />
        );
    };

    const getInsightPackagesCardAction = () => {
        const description = details?.["description"];
        const sidePanelDetailsForCaireRecommendation = buildSidePanelDetails(
            content,
            insightType,
        );

        return (
            <InsightPackagesCardAction
                title={title!}
                details={sidePanelDetailsForCaireRecommendation}
                marketplaceId={marketplaceId!}
                advertiserId={advertiserId!}
                recommendationId={identifier!}
                insightType={insightType}
                description={description}
                index={index}
                metadata={details as DocumentType}
            />
        );
    };

    /**
     * InsightType to provider mapping defined in the model package here
     * https://tiny.amazon.com/esrdiz63/codeamazpackD16Gblob1293mode
     */
    const getAction = () => {
        switch (insightType) {
            case InsightType.CDB_TEMPLATE:
                return getOnDemandInsightCardAction();
            case InsightType.INSIGHT:
                return getInsightFamilyCardAction();
            case InsightType.MARKETING:
                return getMarketingNarrativeCardAction();
            case InsightType.PITCHED_CONTENT:
                return getPitchedContentCardAction();
            case InsightType.PACKAGING:
                return getInsightPackagesCardAction();
            default:
                console.error(`Unsupported content type: ${insightType}`);
                return <></>;
        }
    };

    const getCardHeader = () => {
        switch (insightType) {
            case InsightType.INSIGHT:
                return cardHeader?.includes(InsightFamilyName.INSIGHT_GENIE)
                    ? (
                          details?.metadata as {
                              textContents: { textId: string }[];
                          }
                      )?.textContents?.[0]?.textId
                    : InsightFamilyNameToLabelMap[
                          cardHeader as InsightFamilyName
                      ];
            default:
                return cardHeader;
        }
    };

    return (
        <CardLayoutV3
            cardHeader={getCardHeader()!}
            insightType={insightType}
            insightTypeName={insightTypeLabelToValue[insightType] ?? undefined}
            primaryContext={primaryContext}
            secondaryContext={secondaryContext}
            additionalInfo={additionalInfo as KeyValue}
            lastModifiedDate={lastModifiedDate}
            tag={tag}
            actions={getAction()}
            dataTestId={identifier ?? ""}
            key={identifier}
        />
    );
};
